import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../context/LanguageContext';

// Import des images Immo avec le chemin correct
import immo1 from '../assets/image/Immo/i1.png';
import immo2 from '../assets/image/Immo/i2.png';
import immo3 from '../assets/image/Immo/i3.png';
import immo4 from '../assets/image/Immo/i4.png';
import immo5 from '../assets/image/Immo/i5.png';
import immo6 from '../assets/image/Immo/i6.png';

// Images Boxing Superstar
import bs2 from '../assets/image/BS/bs2.png';
import mcdBs from '../assets/image/BS/mcd.bs.png';
import trello from '../assets/image/BS/trell.png';

// Images DELCO
import delco0 from '../assets/image/DELCO/d0.png';
import delco1 from '../assets/image/DELCO/d1.png';
import delco2 from '../assets/image/DELCO/d2.png';
import delco3 from '../assets/image/DELCO/d3.png';
import delco4 from '../assets/image/DELCO/d4.png';
import delco5 from '../assets/image/DELCO/d5.png';

// Images RF Region France
import rf1 from '../assets/image/RF/r1.png';
import rf2 from '../assets/image/RF/r2.png';
import rf3 from '../assets/image/RF/r3.png';
import rf4 from '../assets/image/RF/Capture d\'écran 2024-01-01 204723.png';

// Images ADO.NET
import ado1 from '../assets/image/ADO/a1.png';
import ado2 from '../assets/image/ADO/a2.png';
import ado3 from '../assets/image/ADO/a3.png';
import ado4 from '../assets/image/ADO/a4.png';

const ProjectImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full h-full">
      <div className="aspect-video w-full overflow-hidden">
        <img
          src={images[currentIndex]}
          alt={`Slide ${currentIndex + 1}`}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      
      {/* Navigation buttons */}
      <button
        onClick={prevImage}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-2 rounded-full transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextImage}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-2 rounded-full transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentIndex ? 'bg-white' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const ProjectCard = ({ title, description, technologies, githubLink, images, reverse }) => {
  const { t } = useContext(LanguageContext);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 bg-white rounded-2xl shadow-xl overflow-hidden`}
    >
      {/* Image du projet */}
      <div className="lg:w-1/2 relative group">
        {images ? (
          <ProjectImageCarousel images={images} />
        ) : (
          <div className="aspect-video w-full overflow-hidden">
            <img
              src={'https://via.placeholder.com/800x600?text=Projet'}
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Contenu du projet */}
      <div className="lg:w-1/2 p-8 flex flex-col justify-between">
        <div>
          <h3 className="text-2xl font-bold text-indigo-900 mb-4">{title}</h3>
          <p className="text-gray-600 mb-6">{description}</p>
          
          {/* Technologies utilisées */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-sm text-gray-500 mr-2">{t('technologiesUsed')}:</span>
            {technologies.map((tech, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-indigo-100 text-indigo-600 rounded-full text-sm font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Lien GitHub */}
        <a
          href={githubLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-indigo-600 hover:text-indigo-800 transition-colors group w-fit"
        >
          <span className="mr-2">{t('viewOnGithub')}</span>
          <svg
            className="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </a>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const { t, lang } = useContext(LanguageContext);

  const projects = [
    {
      title: lang === 'fr' ? "Immo Île-de-France" : t('immoTitle'),
      description: lang === 'fr' 
        ? "Projet réalisé en équipe : Plateforme de gestion d'annonces immobilières en Île-de-France. Interface permettant aux utilisateurs de rechercher, filtrer et publier des annonces immobilières. Utilisation de Trello pour la gestion agile du projet." 
        : t('immoDesc'),
      technologies: ["Symfony", "jQuery", "Bootstrap", "MySQL", "Trello"],
      githubLink: "https://github.com/MLB-78/Immo_Idf",
      images: [immo1, immo2, immo3, immo4, immo5, immo6]
    },
    {
      title: lang === 'fr' ? "Boxing Superstar" : t('boxingTitle'),
      description: lang === 'fr' ? "Application sportive innovante dédiée au développement du cardio. Suivi personnalisé des performances, création de programmes d'entraînement adaptés et visualisation des progrès en temps réel." : t('boxingDesc'),
      technologies: ["React", "Node.js", "MongoDB", "Express", "Chart.js"],
      githubLink: "https://github.com/MLB-78/Boxing_Superstars",
      images: [bs2, mcdBs, trello]
    },
    {
      title: lang === 'fr' ? "DELCO Forum" : t('delcoTitle'),
      description: lang === 'fr' ? "Forum interne pour les employés de DELCO facilitant la communication et le partage de connaissances. Système complet de gestion des utilisateurs, catégorisation des discussions et notifications en temps réel." : t('delcoDesc'),
      technologies: ["Symfony", "Twig", "MySQL", "Bootstrap", "JavaScript"],
      githubLink: "https://github.com/MLB-78/Delco_Forum",
      images: [delco0, delco1, delco2, delco3, delco4, delco5]
    },
    {
      title: lang === 'fr' ? "RF - Region France" : t('rfTitle'),
      description: lang === 'fr' ? "Plateforme web exploitant l'API gouvernementale pour explorer les données géographiques de la France. Interface intuitive permettant la visualisation et l'analyse des données territoriales." : t('rfDesc'),
      technologies: ["Symfony", "Twig", "MySQL", "Bootstrap", "JavaScript"],
      githubLink: "https://github.com/MLB-78/RF_Region-France",
      images: [rf1, rf2, rf3, rf4]
    },
    {
      title: lang === 'fr' ? "Bibliothèque ADO.NET" : t('adoTitle'),
      description: lang === 'fr' ? "Bibliothèque complète en C# offrant des fonctionnalités CRUD pour la gestion de données relationnelles. Architecture modulaire et documentation détaillée pour une intégration facile." : t('adoDesc'),
      technologies: ["C#", "ADO.NET", "SQL Server", "XML", "Unit Testing"],
      githubLink: "https://github.com/MLB-78/TP_Bibliotheque",
      images: [ado1, ado2, ado3, ado4]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-36 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h1 className="text-4xl font-bold text-indigo-900 mb-6">
            {t('projectsTitle')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('projectsIntro')}
          </p>
        </motion.div>

        {/* Grille de projets */}
        <div className="space-y-20">
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              {...project}
              reverse={index % 2 === 1}
            />
          ))}
        </div>

        {/* Section Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-20"
        >
          <p className="text-xl text-gray-600 mb-8">
            {t('interestedProjects')}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
          >
            {t('contactMe')}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Projects; 